import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import SeverityBadge from "./SeverityBadge";
import ServicePill from "./ServicePill";
import LayerBadge from "./LayerBadge";
import { LIFECYCLE_META, LIFECYCLE_ORDER } from "./LifecyclePill";
import { api, fmtUSD } from "../lib/api";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function OpportunityDetailModal({ open, onClose, opportunity, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [localStatus, setLocalStatus] = useState(null);
  if (!opportunity) return null;
  const o = opportunity;
  const lifecycle = localStatus || o.lifecycle_status || "open";

  const changeLifecycle = async (status) => {
    setSaving(true);
    try {
      await api.setLifecycle(o.id, status);
      setLocalStatus(status);
      toast.success("Lifecycle updated", {
        description: `Finding moved to ${LIFECYCLE_META[status]?.label || status}.`,
      });
      onChanged?.();
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setLocalStatus(null); onClose(); } }}>
      <DialogContent
        className="max-w-2xl rounded-sm border-border max-h-[85vh] overflow-y-auto"
        data-testid="opportunity-detail-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 flex-wrap">
            <ServicePill service={o.service} />
            <SeverityBadge severity={o.severity} />
            <LayerBadge layer={o.layer} showName />
            {o.auto_remediable && (
              <Badge className="bg-[#22c55e]/10 text-[#15803d] hover:bg-[#22c55e]/10 rounded-sm border border-[#22c55e]/30 font-medium">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                auto-remediable
              </Badge>
            )}
          </div>
          <DialogTitle className="cs-heading text-xl tracking-tight mt-3" data-testid="modal-title">
            {o.opportunity_type}
          </DialogTitle>
          <DialogDescription className="text-foreground">
            <span className="cs-mono text-sm">{o.resource_name}</span>
            <span className="text-muted-foreground"> · {o.project_id} · {o.location}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-1">
          <div className="grid grid-cols-3 gap-3">
            <div className="border border-border p-3 rounded-sm">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Monthly Savings</div>
              <div className="cs-kpi-value text-xl text-[#00579e] mt-1">{fmtUSD(o.estimated_monthly_savings_usd)}</div>
            </div>
            <div className="border border-border p-3 rounded-sm">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Annual Savings</div>
              <div className="cs-kpi-value text-xl text-foreground mt-1">{fmtUSD((o.estimated_monthly_savings_usd || 0) * 12)}</div>
            </div>
            <div className="border border-border p-3 rounded-sm">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Risk</div>
              <div className="cs-kpi-value text-xl text-foreground mt-1 capitalize">{o.remediation_risk || "—"}</div>
            </div>
          </div>

          {/* Ownership + lifecycle */}
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-border p-3 rounded-sm" data-testid="modal-ownership">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Inferred Owner</div>
              <div className="text-sm font-medium text-foreground">{o.team || "—"}</div>
              <div className="cs-mono text-[12px] text-muted-foreground">{o.owner || "unassigned"}</div>
            </div>
            <div className="border border-border p-3 rounded-sm" data-testid="modal-lifecycle">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Lifecycle Status</div>
              <Select value={lifecycle} onValueChange={changeLifecycle} disabled={saving}>
                <SelectTrigger className="rounded-sm h-8 text-sm" data-testid="lifecycle-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LIFECYCLE_ORDER.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`lifecycle-option-${s}`}>
                      {LIFECYCLE_META[s].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Description</div>
            <p className="text-sm leading-relaxed text-foreground">{o.description}</p>
          </div>

          <div className="border-l-2 border-[#00579e] pl-4 py-1">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Recommendation</div>
            <p className="text-sm leading-relaxed text-foreground">{o.recommendation}</p>
          </div>

          {o.ai_summary && (
            <div className="bg-[hsl(var(--cs-tint))] border border-border p-3 rounded-sm">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">AI Summary</div>
              <p className="text-sm text-foreground italic">{o.ai_summary}</p>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Evidence</div>
            <pre className="cs-mono text-[11px] bg-[#FAFAFA] border border-border p-3 rounded-sm overflow-x-auto leading-relaxed">
{JSON.stringify(o.details || {}, null, 2)}
            </pre>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Resource ID</div>
            <div className="cs-mono text-[11px] text-foreground break-all">{o.resource_id}</div>
          </div>

          {!o.auto_remediable && (
            <div className="flex items-start gap-2 p-3 bg-[#fef3c7] border border-[#f59e0b]/40 rounded-sm">
              <AlertTriangle className="w-4 h-4 text-[#92400e] mt-0.5 shrink-0" />
              <div className="text-xs text-[#92400e]">
                This opportunity is <span className="font-semibold">not auto-remediable</span>. Follow the recommendation manually
                using the GCP console or Terraform.
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose} data-testid="modal-close" className="rounded-sm">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
